import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { CompanyPermission } from "AppSrc/company/permissions";
import { ErrorBoundary } from "storybook-dashboard/errors";

import { MiniErrorBoundary } from "AppSrc/project/report/error";
import { companyFields } from "AppSrc/company/children";
import CompanyProjectForm from "AppSrc/company/projectForm";
import ProjectRow from "./projectRow";
import { Indent } from "./indent";
import { confirmDelete } from "traec-react/utils/sweetalert";

import { setAndShowModal } from "AppSrc/utils/modal";
import { getTerm } from "./utils";
import { SetMetaDataFields } from "AppSrc/forms/meta";

import { useLocalStorage } from "AppSrc/utils/hooks";
import { Modal, useModal } from "storybook-dashboard/components/modal";
import { doFetch } from "storybook-dashboard/utils/fetching";
import { Hide } from "storybook-dashboard/utils";
import Im from "immutable";
import { useTreeContext } from "./context";
import { useIsCurrent } from "./utils";
import { formEventToData } from "../utils";
import useApi from "storybook-dashboard/utils/fetching";
import { SidebarDropdown } from "../company/tree/treeRow";

const hostname = location.hostname;
const is_sizewellOrLocalhost = hostname.includes("localhost") || hostname.includes("sizewellc");

const useTenantMeta = () => {
  const { data } = useApi("/api/tenant/meta/");
  console.log("useTenantMeta", data?.toJS());
  return (data || Im.Map()).get("meta_json");
};

const deleteCompany = (e, companyId, company) => {
  e.preventDefault();

  let redirectUrl = "/accounts/profile";
  if (company) {
    let parentId = (company.get("parentid") || "").substring(0, 8);
    redirectUrl = parentId ? `/company/${parentId}` : redirectUrl;
  }
  console.log("WILL REDIRECT TO", redirectUrl);

  const _companyId = companyId.substring(0, 8);

  confirmDelete({
    text: `This will delete Company (${_companyId}).  ALL MEMBERSHIP DATA, NESTED DATA, NESTED PROJECT DATA AND HISTORICAL REPORTED DATA WILL BE PERMANENTLY LOST. Are you sure you would like to proceed?`,
    onConfirm: () => {
      confirmDelete({
        text: `Are you really really sure?  Data will not be recoverable.`,
        onConfirm: () => {
          confirmDelete({
            text: `...Really?`,
            onConfirm: () => {
              doFetch(`/api/company/${companyId}/`, "DELETE").then((response) => {
                if (response.ok) {
                  location.href = redirectUrl;
                } else {
                  console.error("Failed to delete company", response);
                }
              });
            },
          });
        },
      });
    },
  });
};

function CompanyProjectList({ company, depth }) {
  const { searchTerm, isMatchingItem, hasMatchingDescendant, isDescendantOfMatching } = useTreeContext();
  if (!company) return null;

  let projectList = Im.Set(company.get("projects") || Im.List());
  return projectList
    .filter((project) => {
      if (!project) return false;
      if (!searchTerm) return true;
      return (
        isMatchingItem(project, searchTerm) ||
        hasMatchingDescendant({ projectId: project.get("uid") }) ||
        isDescendantOfMatching(project, "project", project.get("uid"))
      );
    })
    .sortBy((project) => project.get("name"))
    .map((project, i) => <ProjectRow key={i} company={company} project={project} depth={depth + 1} />);
}

function SubCompanyList({ company, depth }) {
  const { getCompanyById, searchTerm, isMatchingItem, hasMatchingDescendant, isDescendantOfMatching } =
    useTreeContext();

  let childIds = company.get("childids");
  if (!childIds) {
    return null;
  }

  return childIds
    .map((id) => getCompanyById(id))
    .filter((child) => {
      if (!child) return false;
      if (!searchTerm) return true;
      return (
        isMatchingItem(child, searchTerm) ||
        hasMatchingDescendant({ companyId: child.get("uid") }) ||
        isDescendantOfMatching(child, "company", child.get("uid"))
      );
    })
    .sortBy((child) => child.get("name"))
    .map((child, i) => <CompanyTreeRow key={i} depth={depth + 1} isRoot={false} company={child} />);
}

const getNewCompanyMeta = (company, tenant_meta) => {
  let inputs =
    Im.fromJS(tenant_meta || Im.Map()).get("new_company_input_details") || company.getInPath("meta_json.input_details");

  let meta = company.get("meta_json") || Im.Map();

  return inputs ? meta.set("input_details", inputs) : meta;
};

const createCompany = (e, { parentId, copyIndicators, meta, onSuccess }) => {
  e.preventDefault();
  let data = formEventToData(e);
  console.log("Creating company with data", data, copyIndicators, meta);

  const url = `/api/company/${copyIndicators ? "?copy_indicators=true" : ""}`;
  doFetch(url, "POST", {
    ...data,
    parentid: parentId || null,
    meta_json: meta?.toJS() || {},
  }).then((response) => (response.ok ? onSuccess(response.data) : console.error("Failed to create company", response)));
};

function AddCompanyWithMetaForm(props) {
  let { company, tenant_meta, modalId, onSuccess } = props;
  company = Im.fromJS(company);

  let [copyIndicators, setCopyIndicators] = useState(true);
  let [meta, setMeta] = useState(getNewCompanyMeta(company, tenant_meta));
  let styles = (meta?.get("styles") || Im.Map()).toJS();

  const _setMeta = (meta) => {
    // Special function for SSEN
    if (meta?.get("Scheme Reference") && meta?.get("Project Name")) {
      companyFields.name.value = `${meta?.get("Scheme Reference")} - ${meta?.get("Project Name")}`;
    }
    setMeta(meta);
  };

  return (
    <Modal title={getTerm("Add a Business Unit", props)}>
      <form onSubmit={(e) => createCompany(e, { parentId: company.get("uid"), copyIndicators, meta, onSuccess })}>
        <div className="form-group">
          <label htmlFor="newCompanyName">Name</label>
          <input className="form-control" id="newCompanyName" name="name" />
        </div>

        <div className="form-group form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="copyIndicatorsCheck"
            checked={copyIndicators}
            onChange={(e) => setCopyIndicators(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="copyIndicatorsCheck">
            Copy indicators from parent
          </label>
        </div>

        <Hide when={!meta?.get("input_details")}>
          <ErrorBoundary>
            <hr />
            <div className="col-sm-12">
              <SetMetaDataFields hideAdmin={true} hideSave={true} metaJson={meta} setMeta={_setMeta} />
            </div>
          </ErrorBoundary>
        </Hide>

        <button className="btn btn-sm btn-primary float-right" type="submit">
          Submit
        </button>
      </form>
    </Modal>
  );
}

function AddCompanyProjectModal({ company, companyId, tenant_meta }) {
  return (
    <Modal title={getTerm("Add a Project", { tenant_meta })}>
      <CompanyProjectForm companyId={companyId} company={company} tenant_meta={tenant_meta} />
    </Modal>
  );
}

const editMetaData = (props) => {
  let { company, companyId } = props;
  let modalId = "CommonCompanyModal001";

  console.log("Showing Company meta-data", company?.toJS());

  setAndShowModal(modalId, {
    title: `${getTerm("Company", props)} meta-data`,
    body: (
      <SetMetaDataFields
        hideAdmin={true}
        saveMetaFetchProps={{
          handler: "company",
          method: "patch",
          params: { companyId },
        }}
        pushMetaFetchProps={{
          handler: "company_dispatch",
          method: "post",
          params: { companyId },
        }}
        metaJson={company.get("meta_json")}
      />
    ),
  });
};

function CompanyAdminDropdown(props) {
  let { companyId, company } = props;
  const tenant_meta = useTenantMeta();
  let { triggerCompanies } = useTreeContext();
  const { setModal, hideModal } = useModal();

  if (!company || !companyId) return null;

  const onCreateCompanySuccess = (data) => {
    triggerCompanies();
    hideModal();
  };

  const onCreateProjectSuccess = (data) => {
    triggerCompanies();
    hideModal();
  };

  let _companyId = companyId.substring(0, 8);

  const dropdownItems = [
    {
      name: getTerm("Add a Business Unit", props),
      onClick: (e) =>
        setModal(<AddCompanyWithMetaForm {...{ company, tenant_meta, onSuccess: onCreateCompanySuccess }} />),
    },
    is_sizewellOrLocalhost
      ? {
          name: getTerm("Add a Contract", props),
          onClick: (e) =>
            setModal(
              <Modal title="Add a contract">
                <CompanyProjectForm contract={true} companyId={companyId} company={company} tenant_meta={tenant_meta} />
              </Modal>
            ),
        }
      : null,
    {
      name: getTerm("Add a Project", props),
      onClick: (e) =>
        setModal(
          <AddCompanyProjectModal {...{ company, companyId, tenant_meta, onSuccess: onCreateProjectSuccess }} />
        ),
    },
    { name: "Go to Settings", linkTo: `/company/${_companyId}/details` },
    { name: `${getTerm("Company", props)} info`, onClick: (e) => editMetaData(props) },
    { name: "Delete", onClick: (e) => deleteCompany(e, companyId, company) },
  ].filter(Boolean);

  return (
    <MiniErrorBoundary>
      <SidebarDropdown dropdownItems={dropdownItems} />
    </MiniErrorBoundary>
  );
}

export default function CompanyTreeRow({ company, isRoot = true, depth = 0, tenant_meta }) {
  // If the company is not provided then use the root company
  const {
    rootCompany,
    searchTerm,
    isMatchingItem,
    hasMatchingDescendant,
    isAncestorOfSelected,
    isDescendantOfMatching,
    ExtraContentComponent,
  } = useTreeContext();
  company = company || rootCompany;

  // Get collapse state from local storage
  const companyId = company?.get("uid");
  const [collapsed, setCollapsed] = useLocalStorage(`sidebar-${companyId}`);

  const isSelected = useIsCurrent({ id: companyId });

  // Auto-expand if selected
  useEffect(() => {
    if (isSelected && collapsed) setCollapsed(false);
  }, [isSelected, collapsed, setCollapsed]);

  // Auto-expand if this company has matching descendants and we're searching
  const shouldAutoExpand = searchTerm && hasMatchingDescendant({ companyId });

  // Auto-expand if this company is an ancestor of the selected item
  const shouldExpandForSelection = isAncestorOfSelected && isAncestorOfSelected(companyId);

  const isExpanded = !collapsed || shouldAutoExpand || shouldExpandForSelection;

  // Hide this company if it doesn't match search, has no matching descendants, AND is not a descendant of a matching item
  const shouldHide =
    searchTerm &&
    !isMatchingItem(company, searchTerm) &&
    !hasMatchingDescendant({ companyId }) &&
    !isDescendantOfMatching(company, "company", companyId);
  let rowClass = isSelected ? "tree-row-active" : "";

  let _companyId = companyId?.substring(0, 8);

  let TitleTag = isRoot ? "b" : "span";

  let hasChildren = (company.get("childids") || Im.List()).size > 0 || (company.get("projects") || Im.List()).size > 0;

  console.log("CompanyTreeRow", company.get("name"), depth, isSelected);

  if (shouldHide) {
    return null;
  }

  return (
    <ErrorBoundary>
      <tr className={rowClass} style={{ height: "1.5rem" }}>
        <td className="m-0 p-0" style={{ verticalAlign: "middle", border: "none", width: "100%", height: "100%" }}>
          <div style={{ display: "flex", alignItems: "stretch", height: "100%", minHeight: "1.5rem" }}>
            <Indent
              depth={depth}
              expanded={isExpanded && hasChildren}
              onClickHandler={(e) => setCollapsed(!collapsed)}
            />
            <div style={{ display: "flex", alignItems: "center" }}>
              <Link to={`/company/${_companyId}`} className="ml-2">
                <TitleTag>{company.get("name")}</TitleTag>
              </Link>
            </div>
          </div>
        </td>

        {ExtraContentComponent && <ExtraContentComponent companyId={companyId} depth={depth} />}

        <td className="m-0 p-0" style={{ verticalAlign: "middle", border: "none", width: "5%" }}>
          <MiniErrorBoundary>
            <Hide when={!isSelected}>
              <CompanyPermission companyId={companyId} requiresAdmin={true}>
                <CompanyAdminDropdown companyId={companyId} company={company} tenant_meta={tenant_meta} />
              </CompanyPermission>
            </Hide>
          </MiniErrorBoundary>
        </td>
      </tr>

      {/* Sub-Project/Company list */}
      <Hide when={!isExpanded}>
        <CompanyProjectList company={company} depth={depth} />
        <SubCompanyList company={company} depth={depth} />
      </Hide>
    </ErrorBoundary>
  );
}
